import { ReactRenderer } from '@tiptap/react'
import { computePosition, flip, shift, offset } from '@floating-ui/dom'
import { MentionList } from './MentionList'
import mockServerData from './mentionMockData.json'

// Mock API function that simulates server search
const mockServerSearch = (query: string): Promise<typeof mockServerData> => {
  return new Promise((resolve) => {
    // Simulate network delay (200-500ms)
    const delay = Math.random() * 1000 + 200

    setTimeout(() => {
      const results = mockServerData
        .filter(item =>
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          item.email.toLowerCase().includes(query.toLowerCase()) ||
          item.role.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 5)

      resolve(results)
    }, delay)
  })
}

// Shared state for tracking loading status
let componentRef: ReactRenderer | null = null

export const mentionSuggestionOptions = {
  // Called before items are fetched - perfect for setting loading state
  onBeforeStart: () => {
    if (componentRef) {
      componentRef.updateProps({ items: [], loading: true })
    }
  },

  items: async ({ query }: { query: string }) => {
    // Return empty array if no query
    if (!query) {
      return []
    }

    // Call mock server API
    const results = await mockServerSearch(query)

    return results
  },

  render: () => {
    let component: ReactRenderer
    let popup: HTMLElement

    return {
      onStart: (props: any) => {
        component = new ReactRenderer(MentionList, {
          props: { ...props, loading: true },
          editor: props.editor,
        })
        componentRef = component

        if (!props.clientRect) {
          return
        }

        popup = document.createElement('div')
        popup.style.position = 'absolute'
        popup.style.zIndex = '1000'
        popup.appendChild(component.element)
        document.body.appendChild(popup)

        const updatePosition = () => {
          if (!props.clientRect) return

          computePosition(
            {
              getBoundingClientRect: props.clientRect,
            },
            popup,
            {
              placement: 'bottom-start',
              middleware: [
                offset(8),
                flip(),
                shift({ padding: 8 }),
              ],
            }
          ).then(({ x, y }) => {
            popup.style.left = `${x}px`
            popup.style.top = `${y}px`
          })
        }

        updatePosition()
      },

      onUpdate(props: any) {
        // Flip loading indicator off when we have items, keep it on if list is empty while fetching
        const isLoading = !props.items || props.items.length === 0
        component.updateProps({ ...props, loading: isLoading })

        if (!props.clientRect || !popup) {
          return
        }

        computePosition(
          {
            getBoundingClientRect: props.clientRect,
          },
          popup,
          {
            placement: 'bottom-start',
            middleware: [
              offset(8),
              flip(),
              shift({ padding: 8 }),
            ],
          }
        ).then(({ x, y }) => {
          popup.style.left = `${x}px`
          popup.style.top = `${y}px`
        })
      },

      onKeyDown(props: { event: KeyboardEvent }) {
        if (props.event.key === 'Escape') {
          if (popup) {
            popup.remove()
          }
          return true
        }

        return (component.ref as any)?.onKeyDown(props)
      },

      onExit() {
        componentRef = null
        if (popup) {
          popup.remove()
        }
        if (component) {
          component.destroy()
        }
      },
    }
  },
}
